import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Button, Checkbox, Flex, Input } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { FILTER_PANEL_VARIANTS } from "../constants"
import { includesFilterId, toggleFilterId } from "../utils"
import styles from "./index.module.css"

/**
 * A search box over one or more groups of checkboxes — the shape every "pick some of these"
 * filter takes: crew and equipment, listings, experiences.
 *
 * The panel stays mounted inside its dropdown, so the local draft re-syncs whenever the applied
 * selection changes from outside — otherwise reopening it would show a stale tick.
 */
const CheckboxFilterPanel = ({
  groups,
  selectedIds,
  onApply,
  onClose,
  title,
  variant = FILTER_PANEL_VARIANTS.DROPDOWN,
  hasFooter = true,
  className = ""
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [draftIds, setDraftIds] = useState(selectedIds)

  useEffect(() => setDraftIds(selectedIds), [selectedIds])

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase()
    const matches = (option) => `${option.label} ${option.suffix || ""}`.toLowerCase().includes(query)
    return groups.map((group) => ({ ...group, options: group.options.filter(matches) })).filter((group) => group.options.length > 0)
  }, [groups, search])

  /** Without a footer there is nothing to confirm, so the draft is applied as it changes. */
  const commit = (ids) => {
    setDraftIds(ids)
    if (!hasFooter) onApply(ids)
  }

  const isInline = variant === FILTER_PANEL_VARIANTS.INLINE

  return (
    <div className={`${isInline ? styles.inlinePanel : styles.panel} ${className}`}>
      {title && <span className={styles.title}>{title}</span>}

      <Input
        prefix={<Icon name="Search" width={16} height={16} color="var(--color-text-secondary)" />}
        placeholder={t("filters.search", { defaultValue: "Search" })}
        className={styles.search}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        allowClear
      />

      <div className={styles.scroll}>
        {!filteredGroups.length && <span className={styles.empty}>{t("filters.no_results", { defaultValue: "No results" })}</span>}
        {filteredGroups.map((group) => (
          <div key={group.key} className={styles.group}>
            {group.label && (
              <Flex align="center" gap="var(--spacing-xxs)" className={styles.groupLabel}>
                {group.iconName && <Icon name={group.iconName} width={16} height={16} color="var(--color-text-secondary-dark)" />}
                <span>{group.label}</span>
              </Flex>
            )}
            {group.options.map((option) => {
              const isChecked = includesFilterId(draftIds, option.id)
              return (
                <Checkbox
                  key={option.id}
                  checked={isChecked}
                  onChange={() => commit(toggleFilterId(draftIds, option.id))}
                  className={`${styles.option} ${isChecked ? styles.optionChecked : ""}`}
                >
                  {option.label}
                  {option.suffix && <span className={styles.optionSuffix}> {option.suffix}</span>}
                </Checkbox>
              )
            })}
          </div>
        ))}
      </div>

      {hasFooter && (
        <Flex gap="var(--spacing-xs)" className={styles.footer}>
          <Button
            block
            shape="round"
            onClick={() => {
              setDraftIds([])
              onApply([])
              onClose?.()
            }}
          >
            {t("filters.clear", { defaultValue: "Clear" })}
          </Button>
          <Button
            block
            type="primary"
            shape="round"
            onClick={() => {
              onApply(draftIds)
              onClose?.()
            }}
          >
            {t("filters.apply", { defaultValue: "Apply" })}
          </Button>
        </Flex>
      )}
    </div>
  )
}

CheckboxFilterPanel.propTypes = {
  /** One group per labelled section. A single group with no `label` renders as a plain list. */
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      /** A DS icon name shown beside the group label. */
      iconName: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          label: PropTypes.string.isRequired,
          /** Greyed trailing detail, e.g. a crew member's role. Searchable with the label. */
          suffix: PropTypes.string
        })
      ).isRequired
    })
  ).isRequired,
  selectedIds: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  /** Heading above the search box; omitted when the surrounding surface already has one. */
  title: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(FILTER_PANEL_VARIANTS)),
  /** Adds Clear/Apply and defers applying until Apply is pressed. */
  hasFooter: PropTypes.bool,
  className: PropTypes.string
}

export default CheckboxFilterPanel
